import type { LanguageCode } from "@/lib/languages"
import type { InjectedColorMode, UiOptionKey } from "@/lib/runtime-branding"

import { api } from "@/lib/api-client"

export type BrandingLinkPlacement = "chat_footer"

export type BrandingLink = {
  id: string
  label: string
  url: string
  placement: BrandingLinkPlacement
}

export type BrandingCssVariables = {
  light?: Record<string, string>
  dark?: Record<string, string>
}

export type BrandingAssetRef = {
  assetKey: string
  url: string
  versionedUrl: string
  contentType: string
  kind: string
  sizeBytes: number
  width: number
  height: number
  version: string
  uploadedAt: string
  uploadedBy: string
}

export type VoiceStageInk = "dark" | "light"

/** The ground behind the voice window; null in storage means the brand's sky. */
export type VoiceStageSetting =
  | { mode: "sky" }
  | { mode: "color"; color: string }
  | { mode: "image"; ink: VoiceStageInk }

/** Per-deployment UI switches; an absent key follows the build (#3225). */
export type BrandingUiOptions = Partial<Record<UiOptionKey, boolean>>

export type BrandingSettings = {
  assistant_name: string | null
  app_name: string | null
  organization_name: string | null
  css_variables: BrandingCssVariables
  theme_color: string | null
  manifest_name: string | null
  manifest_short_name: string | null
  assets: Record<string, unknown>
  links: BrandingLink[]
  voice_stage: VoiceStageSetting | null
  ui_options: BrandingUiOptions
  /** Interface languages offered, in switcher order; null for all (#3226). */
  enabled_languages: LanguageCode[] | null
  /** Privacy policy linked from the onboarding consent text; null for the default (#3243). */
  privacy_policy_url: string | null
  /** Color mode for users with no stored choice; null means light (#3298). */
  default_color_mode: InjectedColorMode | null
  resolved_assistant_name: string
}

export type BrandingPatch = Partial<Omit<BrandingSettings, "assets" | "resolved_assistant_name">>

export type BrandingAssetsList = {
  assets: Record<string, BrandingAssetRef | null>
  allowedKinds: Record<string, string[]>
  maxUploadBytes: number
}

export async function getBrandingSettings(): Promise<BrandingSettings> {
  const response = await api.get("/admin/branding")
  return response.data || response
}

export async function updateBrandingSettings(patch: BrandingPatch): Promise<BrandingSettings> {
  const response = await api.patch("/admin/branding", patch)
  return response.data || response
}

export async function listBrandingAssets(): Promise<BrandingAssetsList> {
  const response = await api.get("/admin/branding/assets")
  return response.data || response
}

export async function uploadBrandingAsset(assetKey: string, file: File): Promise<BrandingAssetRef> {
  const form = new FormData()
  form.append("file", file)
  const response = await api.post(`/admin/branding/assets/${encodeURIComponent(assetKey)}`, form)
  return response.data || response
}

export async function deleteBrandingAsset(assetKey: string): Promise<void> {
  await api.delete(`/admin/branding/assets/${encodeURIComponent(assetKey)}`)
}
